/**
 * Management agreements (docs/11 F-2; closes O-34).
 *
 * TKC Management Services' own contract with an owner. NOT a vendor agreement:
 * that is TKC buying a service, this is TKC selling one, and the rates here are
 * the rates we BILL.
 *
 * Matt's rule about the fields, 2026-08-24: *"while these are all really good
 * fields to carry, they should not all be required because each management
 * agreement can vary greatly between property types, ownership types, etc."* So
 * everything below is nullable except the name and the scope — the two facts
 * without which the record cannot be filed or found.
 */

import { z } from 'zod';

import { partialUpdateSchema } from './partial-update.ts';

/**
 * The scope. Exactly one: a property, or a group where one agreement covers
 * several. "Which agreement governs this property" must have one answer, which
 * is the ADR-42 lesson applied to a different table.
 */
export const managementScopeSchema = z
  .object({
    propertyId: z.uuid().nullish(),
    propertyGroupId: z.uuid().nullish(),
  })
  .refine(
    (scope) =>
      (scope.propertyId === null || scope.propertyId === undefined) !==
      (scope.propertyGroupId === null || scope.propertyGroupId === undefined),
    { message: 'Choose either a property or a group, not both' },
  );

/** Cents, non-negative, and big enough that nothing overflows a real contract. */
const cents = z.number().int().min(0).max(9_999_999_999);

export const managementWriteSchema = z.object({
  name: z.string().trim().min(1).max(200),
  propertyId: z.uuid().nullish(),
  propertyGroupId: z.uuid().nullish(),
  /** The term. Both nullable — an evergreen agreement is normal. */
  startsOn: z.iso.date().nullish(),
  endsOn: z.iso.date().nullish(),
  /**
   * ALWAYS present in a real agreement (Matt) but not enforced: an agreement
   * being entered before its fee is confirmed is a worse failure than a null.
   */
  managementFeePerSqFtPerYearCents: cents.nullish(),
  laborRatePerHourCents: cents.nullish(),
  overtimeRatePerHourCents: cents.nullish(),
  /** Sometimes present, sometimes not (Matt) — which is why it is nullable. */
  tripChargeCents: cents.nullish(),
  /** Duties and obligations, as a person summarises contract clauses. */
  duties: z.string().trim().max(8000).nullish(),
  /**
   * THE ABSTRACT — the summarised terms, as a lease abstract summarises a lease
   * (0055). Matt, 2026-08-26: management agreements should carry what leases
   * carry, and he named "notes, abstract notes" separately. `duties` is what we
   * are obliged to do; this is everything else the contract says that somebody
   * reads instead of the contract.
   */
  abstractNotes: z.string().trim().max(16_000).nullish(),
  /** What people said about it, as opposed to what it says. */
  notes: z.string().trim().max(4000).nullish(),
  criticalInformation: z.string().trim().max(4000).nullish(),
});
export type ManagementWrite = z.infer<typeof managementWriteSchema>;

export const managementCreateSchema = managementWriteSchema.refine(
  (input) =>
    (input.propertyId === null || input.propertyId === undefined) !==
    (input.propertyGroupId === null || input.propertyGroupId === undefined),
  { message: 'Choose either a property or a group, not both' },
);

/** The scope never moves: re-scoping an agreement is a different agreement. */
export const managementUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: partialUpdateSchema(
    managementWriteSchema.omit({ propertyId: true, propertyGroupId: true }),
  ),
});

export const managementListQuerySchema = z.object({
  propertyId: z.uuid().optional(),
  propertyGroupId: z.uuid().optional(),
  /** Defaults to active: an ended agreement is history, not the answer. */
  status: z.enum(['active', 'superseded', 'inactive', 'any']).default('active'),
});

export const managementInactivateSchema = z.object({
  reason: z.string().trim().min(3).max(500),
});

/**
 * Supersession: retire THIS agreement and file the one that replaces it, in one
 * call. Matt, 2026-08-26: *"supersede with previous agreement going to inactive
 * for viewing later"*.
 *
 * `:id` is the PREDECESSOR and the body carries the successor, which looks
 * backwards until you follow the constraint: the scope has a unique index on its
 * ACTIVE agreement (0034), so two active agreements on one property cannot
 * coexist — not for a transaction, not for a moment. A "link these two existing
 * agreements" endpoint would therefore be unreachable: the second one could
 * never have been created. The renewal has to be born as the replacement.
 *
 * The successor inherits the predecessor's SCOPE rather than naming its own:
 * superseding across properties is not a renewal, and the scope is the one fact
 * that must not move (`managementUpdateSchema` omits it for the same reason).
 *
 * The predecessor becomes `superseded`, not `inactive`: the two answer different
 * questions — inactive is "we are not managing this any more", superseded is
 * "this is the previous version of a contract we still hold" — and only one of
 * them should put the property on the unmanaged list.
 */
export const managementSupersedeSchema = z.object({
  version: z.number().int().positive(),
  replacement: managementWriteSchema.omit({ propertyId: true, propertyGroupId: true }),
});

export interface ManagementAgreementRow {
  readonly id: string;
  readonly name: string;
  readonly propertyId: string | null;
  readonly propertyGroupId: string | null;
  /** The scope's name, resolved server-side so no list needs a second fetch. */
  readonly scopeName: string | null;
  readonly startsOn: string | null;
  readonly endsOn: string | null;
  readonly managementFeePerSqFtPerYearCents: number | null;
  readonly laborRatePerHourCents: number | null;
  readonly overtimeRatePerHourCents: number | null;
  readonly tripChargeCents: number | null;
  readonly status: 'active' | 'superseded' | 'inactive';
  readonly updatedAt: string;
  readonly version: number;
}

export interface ManagementAgreementDetail extends ManagementAgreementRow {
  readonly duties: string | null;
  readonly abstractNotes: string | null;
  readonly notes: string | null;
  readonly criticalInformation: string | null;
  readonly inactivatedReason: string | null;
  readonly canManage: boolean;
  /** `management.supersede`, its own key (0055) — see ADR-60. */
  readonly canSupersede: boolean;
  /** Set on a superseded row: what replaced it, so the trail runs forwards. */
  readonly supersededById: string | null;
  readonly supersededByName: string | null;
  /** Set on the successor: what it replaced, so the trail runs backwards too. */
  readonly supersedesId: string | null;
  readonly supersedesName: string | null;
}
